import { useState, useEffect, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import routes from "routes";
import { Amplify } from "aws-amplify";
import config from "./cognito.json";
import { Authenticator, useTheme, View, Heading, Text, Image } from "@aws-amplify/ui-react";
import "./styles.css";
import { useSoftUIController, setMiniSidenav, setOpenConfigurator, setTransparentSidenav, setFixedNavbar, setSidenavColor } from "context";
import { fetchUserAttributes } from 'aws-amplify/auth';
import "./scssc/coreui.scss"; //Estilos propios de core ui-react.

Amplify.configure(config);
const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [divimage, setdivimage] = useState(false); // Estado para verificar autenticación
  const [isLoading, setIsLoading] = useState(true); // Estado para manejar la carga inicial
  const { pathname } = useLocation();

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Simula la verificación del estado de autenticación
  useEffect(() => {
    // Simulamos un retardo para verificar la autenticación
    const checkAuthentication = () => {
      // Aquí podrías realizar la verificación real con tu sistema de autenticación
      setTimeout(() => {
        // Simulamos que no está autenticado
        setIsLoading(false); // Cambia el estado de carga a false una vez verificada la autenticación
      }, 1300); // Simulando un retardo de 2 segundos para la verificación
    };
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      setTimeout(() => {
        setdivimage(true);
      }, 110);
    }
  }, [isAuthenticated, isLoading]);

  // Agrega este useEffect para ajustar el overflow de body y html
  useEffect(() => {
    if (!isAuthenticated) {
      if (window.innerWidth <= 768) {
        // Permitir desplazamiento vertical en dispositivos móviles (<=768px) cuando no está autenticado
        document.documentElement.style.overflowY = "auto";
        document.body.style.overflowY = "auto";
        document.documentElement.style.height = "100%";
        document.body.style.height = "100%";
      } else {
        // Ocultar barra de desplazamiento en dispositivos con ancho mayor a 768px
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
      }
    } else {
      // Restaurar la barra de desplazamiento cuando está autenticado
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "auto";
    }

    /*
    const handleResize = () => {
      if (!isAuthenticated && window.innerWidth <= 768) {
        document.documentElement.style.overflowY = "auto";
        document.body.style.overflowY = "auto";
        document.documentElement.style.height = "100%";
        document.body.style.height = "100%";
      }
    };
    window.addEventListener("resize", handleResize);
    */

    // Limpiar efecto cuando el componente se desmonte
    return () => {
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "auto";
    };
  }, [isAuthenticated]);


  useEffect(() => {
    if (isAuthenticated) {
      fetchUserAttributes().then(attributes => {
        if (attributes["custom:nav_color"]) {
          setSidenavColor(dispatch, attributes["custom:nav_color"]);
        }
        if (attributes["custom:nav_type"]) {
          setTransparentSidenav(dispatch, attributes["custom:nav_type"] === "transparent");
        }
        if (attributes["custom:nav_anim"]) {
          setFixedNavbar(dispatch, attributes["custom:nav_anim"] === "true");
        }
      }).catch(err => console.log("Error loading preferences from Cognito", err));
    }
  }, [isAuthenticated, dispatch]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <SoftBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="default" color="inherit">
        settings
      </Icon>
    </SoftBox>
  );

  const formFields = {
    signIn: {
      username: {
        label: "Cédula / Nit:",
        placeholder: "Numero sin puntos",
      },
      password: {
        label: "Contraseña:",
        placeholder: "Mayuscula Minuscula Numero Simbolo",
      },
    },
    signUp: {
      name: {
        label: "Nombre u Organización:",
        placeholder: "Nombre completo:",
        isRequired: true,
        order: 1,
      },

      email: {
        label: "Email:",
        placeholder: "Coloca tu email:",
        type: "email",
        isRequired: true,
        order: 2,
      },

      phone_number: {
        label: "Celular",
        placeholder: "Celular",
        isRequired: true,
        dialCode: "+57",
        order: 3,
      },

      profile: {
        label: "Cédula / Nit",
        placeholder: "Numero sin puntos",
        type: "label",
        isRequired: true,
        order: 4,
      },

      username: {
        label: "Confirmar Cédula / Nit",
        placeholder: "Numero sin puntos",
        type: "label",
        isRequired: true,
        order: 5,
      },

      password: {
        label: "Contraseña:",
        placeholder: "Mayuscula Minuscula Numero Simbolo:",
        type: "password",
        isRequired: false,
        order: 6,
      },

      confirm_password: {
        label: "Confirmar Contraseña:",
        order: 7,
      },
    },
    forceNewPassword: {
      password: {
        label: "Contraseña:",
        placeholder: "Ingresa tu contraseña:",
      },
    },
    forgotPassword: {
      username: {
        label: "Cédula / Nit",
        placeholder: "Numero sin puntos:",
      },
    },
    confirmResetPassword: {
      confirmation_code: {
        placeholder: "Ingrese su Código de Confirmación:",
        label: "Codigo",
        isRequired: false,
      },
      password: {
        label: "Nueva Contraseña",
        placeholder: "Mayuscula Minuscula Numero Simbolo:",
      },
      confirm_password: {
        label: "Confirmar Contraseña",
        placeholder: "Mayuscula Minuscula Numero Simbolo:",
      },
    },
    setupTotp: {
      QR: {
        totpIssuer: "test issuer",
        totpUsername: "amplify_qr_test_user",
      },
      confirmation_code: {
        label: "SMS",
        placeholder: "Ingrese su Código de Confirmación:",
        isRequired: false,
      },
    },
    confirmSignIn: {
      confirmation_code: {
        label: "SMS",
        placeholder: "Ingrese su Código de Confirmación:",
        isRequired: false,
      },
    },
  };

  const components = {
    Header() {
      const { tokens } = useTheme();

      return (
        <View textAlign="center" padding={tokens.space.large}>
          <Image
            alt="Amplify logo"
            src="https://s3.amazonaws.com/www.codeflex.lat/documentos/c3320017-1937-4353-8881-475e3c89e25e/Paginas/logo2.png"
            style={{ width: "220px" }}
          />
        </View>
      );
    },

    Footer() {
      const { tokens } = useTheme();

      return (
        <View textAlign="center" padding={tokens.space.large}>
          <Text style={{ color: "black", fontSize: "18px" }}>
            &copy; All Rights Reserved | Codeflex
          </Text>
        </View>
      );
    },

    ForgotPassword: {
      Header() {
        const { tokens } = useTheme();
        return (
          <Heading padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`} level={3}>
            <center style={{ fontSize: "27px" }}>Restablecer la contraseña</center>
          </Heading>
        );
      },
    },

  };

  return (
    <div>
      {/* Mostrar un indicador de carga mientras se verifica la autenticación */}
      {isLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          <div>Loading...</div>
        </div>
      )}

      {/* Interfaz principal si el usuario está autenticado y la verificación ha terminado */}
      {!isLoading && isAuthenticated && (
        <div>
          <Suspense fallback={loading}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              {layout === "dashboard" && (
                <>
                  <Sidenav
                    color={sidenavColor}
                    brand="https://codeflex.com.co/assets/img/modificacion2.png"
                    brandName="CLOUD STORAGE"
                    routes={routes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                  <Configurator />
                </>
              )}
              <Routes>
                {getRoutes(routes)}
                <Route path="*" element={<Navigate to="/Panel" />} />
              </Routes>
            </ThemeProvider>
          </Suspense>
        </div>
      )}

      {/* Sección de autenticación visible solo cuando el usuario no está autenticado y no está en modo de carga */}
      {!isLoading && !isAuthenticated && (
        <section
          id="auth-section"
          style={{
            display: "flex",
            height: "100vh",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden", // Evita la barra de desplazamiento en el contenedor principal
          }}
        >
          {divimage && (
            <div
              id="image-container"
              style={{
                position: "fixed",
                top: "0",
                left: "0",
                width: "50%",
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <img
                src="https://s3.amazonaws.com/www.codeflex.lat/documentos/a423d4ef-3dfb-40a1-9b02-d74ec10e9329/A-A/login.png"
                alt="Login Side Image"
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "cover",
				  objectPosition: "center top",
                }}
              />
              {/* Texto para términos de servicio */}
              <div
                style={{
                  position: "absolute",
                  bottom: "20px",
                  width: "90%",
                  textAlign: "center",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  padding: "10px",
                  borderRadius: "5px",
                  color: "white",
                  fontSize: "16px",
                }}
              >
                <a
                  href="https://codeflex.com.co/#/tos"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "white", textDecoration: "underline" }}
                >
                  Antes de registrarte por favor revisa nuestros términos de servicio
                </a>
              </div>
            </div>
          )}

          <div
            id="auth-container"
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-10%)",
              width: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              padding: "1rem",
              boxSizing: "border-box",
              overflowY: "auto",
            }}
          >
            <div
              id="auth-container2"
              style={{
                width: "100%",
                maxWidth: "25.625rem",
                padding: "2rem",
                borderRadius: "0.375rem",
                boxSizing: "border-box",
                height: "auto", // Permite que el contenido se expanda según sea necesario
                maxHeight: "100%", // Asegura que no exceda la altura del contenedor
              }}
            >
              <Authenticator formFields={formFields} components={components}>
                {({ signOut, user }) => {
                  if (user) {
                    setTimeout(() => {
                      if (!isAuthenticated) setIsAuthenticated(true);
                    }, 0);
                    return null;
                  } else {
                    return (
                      <Suspense fallback={loading}>
                        {/* El formulario de autenticación se muestra aquí si el usuario no está autenticado */}
                      </Suspense>
                    );
                  }
                }}
              </Authenticator>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
